import { Component, Input, OnInit, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CustomFieldType, CustomField, CustomFieldValidationType } from '@fgb/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgbDateNativeAdapter, NgbDateStruct, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';

/**
 * A generic form control component that renders a custom field based on its `FieldType`.
 *
 * Implements `ControlValueAccessor` so it integrates seamlessly with Angular Reactive Forms
 * via `formControlName` — no extra wiring needed in the parent form.
 *
 * Supported field types:
 * - `FieldType === CustomFieldType.Checkbox` — renders as a checkbox (boolean value)
 * - All other types — renders as a text input (string value)
 *
 * To add a new field type, extend the template and update `isCheckbox` or add a new type guard getter.
 */
@Component({
  selector: 'fgbcl-custom-field',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NgbModule],
  templateUrl: './custom-field.component.html',
  styleUrls: ['./custom-field.component.scss'],
  providers: [NgbDateNativeAdapter],
})
export class CustomFieldComponent implements ControlValueAccessor, OnInit {
  @Input() field: CustomField;
  innerValue: string | boolean = '';
  isDisabled = false;
  readonly customValidationType = CustomFieldValidationType;

  minDate: NgbDateStruct | null;
  date: NgbDateStruct | null;

  private _onChange = (_: string | boolean) => {};
  private _onTouched = () => {};

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private adapter: NgbDateNativeAdapter
  ) {
    // Self-register as the value accessor so the parent formControlName binding works without an extra directive.
    if (this.ngControl) this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    if (this.field?.ValidationType === CustomFieldValidationType.Date) {
      this.date = this.adapter.fromModel(new Date());
      this.minDate = this.date;
      this.onDateChange(this.date);
    }
  }

  /** Sets the initial or programmatic value from the parent form control. */
  writeValue(value: string | boolean): void {
    this.innerValue = value ?? (this.isCheckbox ? false : '');
  }

  /** Registers the callback that notifies the parent form when the value changes. */
  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  /** Registers the callback that marks the parent form control as touched. */
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  /** Propagates the disabled state from the parent form control to the input. */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /** Called by the template when the input value changes — notifies the parent form. */
  onValueChange(value: string | boolean): void {
    this.innerValue = value;
    this._onChange(value);
  }

  /** Called by the template on blur — marks the parent form control as touched. */
  onBlur(): void {
    this._onTouched();
  }

  onDateChange(event: NgbDateStruct | null): void {
    const value = DateTime.fromObject({ year: event?.year, month: event?.month, day: event?.day }, { zone: 'utc' }).toFormat(
      'yyyy-MM-dd'
    );
    this.onValueChange(value);
  }

  /** True if the field should render as a checkbox (FieldType === CustomFieldType.Checkbox). */
  get isCheckbox(): boolean {
    return this.field?.FieldType === CustomFieldType.Checkbox;
  }

  /** The underlying Angular form control, used for validation state access in the template. */
  get control() {
    return this.ngControl?.control;
  }

  /** True if the control has a validation error and has been interacted with. */
  get isInvalid(): boolean {
    return !!(this.control?.invalid && this.control?.touched);
  }

  get validationName(): string {
    switch (this.field?.ValidationType) {
      case CustomFieldValidationType.Text:
        return 'text';
      case CustomFieldValidationType.Number:
        return 'number';
      case CustomFieldValidationType.Date:
        return 'date';
      case CustomFieldValidationType.Email:
        return 'email';
      case CustomFieldValidationType.Checkbox:
        return 'checkbox';
      default:
        return 'custom';
    }
  }
}
